import React, { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { aboutAPI } from '../api';

const FALLBACK = {
  eyebrow: 'About Paisley',
  title: 'Paisley on the Web',
  title_highlight: 'Web',
  paragraph_1: 'Paisley, located in Renfrewshire, Scotland, is a historic town celebrated for its rich cultural heritage and architectural wonders.',
  paragraph_2: "Famed for the Paisley Pattern, a textile design that became synonymous with its name, the town boasts various landmarks and a vibrant arts scene that thrives with venues and the annual Sma' Shot Festival.",
  image_url: '/api/files/paisley-org/wp-content/uploads/2025/09/Paisley-skyline-from-abbey-770x513-1.jpg',
  image_alt: 'Paisley Heritage',
  benefits: [
    'Rich cultural heritage and architectural wonders',
    'Famous for the iconic Paisley Pattern textile design',
    'Home to historic Paisley Abbey dating back to the 12th century',
    'Vibrant arts scene with multiple venues and festivals',
    'Largest town in Renfrewshire with thriving economy',
  ],
  visible: true,
};

const renderTitleWithHighlight = (title, highlight) => {
  if (!highlight || !title.toLowerCase().includes(highlight.toLowerCase())) return title;
  const idx = title.toLowerCase().indexOf(highlight.toLowerCase());
  return (
    <>
      {title.slice(0, idx)}
      <span className="font-serif-accent italic font-light bg-gradient-to-r from-sky-500 to-indigo-500 bg-clip-text text-transparent">
        {title.slice(idx, idx + highlight.length)}
      </span>
      {title.slice(idx + highlight.length)}
    </>
  );
};

const About = () => {
  const [c, setC] = useState(FALLBACK);
  useEffect(() => { aboutAPI.get().then((r) => setC({ ...FALLBACK, ...(r.data || {}) })).catch(() => {}); }, []);
  if (c.visible === false) return null;
  const imgSrc = c.image_url && c.image_url.startsWith('http')
    ? c.image_url
    : `${process.env.REACT_APP_BACKEND_URL}${c.image_url || FALLBACK.image_url}`;

  return (
    <section id="about" className="relative py-24 md:py-32" data-testid="about-section">
      <div className="aurora-orb w-[480px] h-[480px] top-32 -left-32 bg-orange-200/60" />
      <div className="aurora-orb w-[400px] h-[400px] bottom-0 -right-20 bg-indigo-200/60" />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="glass-strong rounded-[2.5rem] p-6 md:p-12 lg:p-16">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-center">
            {/* Image */}
            <div className="md:col-span-6 relative">
              <div className="absolute -inset-3 bg-gradient-to-br from-sky-300/40 via-indigo-300/30 to-orange-200/40 rounded-[2rem] blur-xl" />
              <div className="relative rounded-[1.75rem] overflow-hidden ring-1 ring-white/60 shadow-xl">
                <img
                  src={imgSrc}
                  alt={c.image_alt || FALLBACK.image_alt}
                  className="w-full h-[480px] object-cover"
                  data-testid="about-image"
                  onError={(e) => { e.target.src = FALLBACK.image_url; }}
                />
              </div>
            </div>
            {/* Copy */}
            <div className="md:col-span-6">
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-sky-600 mb-5">{c.eyebrow || FALLBACK.eyebrow}</p>
              <h2 className="text-4xl md:text-5xl font-bold leading-[1.05] tracking-[-0.04em] text-slate-900 mb-6" data-testid="about-title">
                {renderTitleWithHighlight(c.title || FALLBACK.title, c.title_highlight || '')}
              </h2>
              {c.paragraph_1 && <p className="text-base md:text-lg text-slate-600 mb-4 leading-relaxed whitespace-pre-line">{c.paragraph_1}</p>}
              {c.paragraph_2 && <p className="text-base md:text-lg text-slate-600 mb-7 leading-relaxed whitespace-pre-line">{c.paragraph_2}</p>}
              {(c.benefits || []).length > 0 && (
                <ul className="space-y-3" data-testid="about-benefits">
                  {(c.benefits || []).filter(Boolean).map((benefit, index) => (
                    <li key={index} className="flex items-start gap-3 group">
                      <span className="shrink-0 w-6 h-6 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center shadow-sm shadow-sky-500/30 ring-2 ring-white">
                        <Check className="text-white" size={13} strokeWidth={3} />
                      </span>
                      <p className="text-slate-700 leading-snug">{benefit}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
